/**
 * ToolList Component (Task 6.2)
 *
 * Displays tools grouped by body part with collapsible sections.
 * Shows tool count per group and allows CRUD operations on tools.
 *
 * Features:
 * - Display tools grouped by body part (head, body, leftArm, rightArm, leftLeg, rightLeg)
 * - Collapsible groups using Radix Collapsible
 * - Show tool count per group
 * - Add Tool button at top
 *
 * Note: Control tools (Wait, Stop) are global and not part of puppets.
 * They are managed separately in toolStore and shown in the sidebar.
 */

import { useState, useMemo } from 'react';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Collapsible from '@radix-ui/react-collapsible';
import { usePuppetStore } from '@/stores/puppetStore';
import type { ToolDefinition, BodyPart } from '@/stores/toolStore';
import { ToolListItem } from './ToolListItem';

/**
 * Body part display order and labels
 * Note: 'control' is excluded - control tools are global, not puppet-specific
 */
type MovementBodyPart = Exclude<BodyPart, 'control'>;

const BODY_PART_CONFIG: {
  key: MovementBodyPart;
  label: string;
  color: string;
}[] = [
  { key: 'head', label: 'Head', color: 'var(--color-body-head)' },
  { key: 'body', label: 'Body', color: 'var(--color-body-body)' },
  { key: 'leftArm', label: 'Left Arm', color: 'var(--color-body-arm)' },
  { key: 'rightArm', label: 'Right Arm', color: 'var(--color-body-arm)' },
  { key: 'leftLeg', label: 'Left Leg', color: 'var(--color-body-leg)' },
  { key: 'rightLeg', label: 'Right Leg', color: 'var(--color-body-leg)' },
];

interface ToolListProps {
  /**
   * The puppet ID whose tools are being displayed
   */
  puppetId: string;

  /**
   * Callback when Add Tool button is clicked
   */
  onAddTool: () => void;

  /**
   * Callback when Edit button is clicked on a tool
   */
  onEditTool: (tool: ToolDefinition) => void;

  /**
   * Callback when Delete button is clicked on a tool
   */
  onDeleteTool: (tool: ToolDefinition) => void;

  /**
   * Callback when Duplicate button is clicked on a tool
   */
  onDuplicateTool: (tool: ToolDefinition) => void;
}

export function ToolList({
  puppetId,
  onAddTool,
  onEditTool,
  onDeleteTool,
  onDuplicateTool,
}: ToolListProps) {
  // Track which groups are expanded (default all expanded)
  const [expandedGroups, setExpandedGroups] = useState<Set<BodyPart>>(
    new Set(BODY_PART_CONFIG.map((config) => config.key))
  );

  // Get tools from the puppet
  const puppet = usePuppetStore((state) =>
    state.puppets.find((p) => p.id === puppetId)
  );
  const tools = puppet?.tools || [];

  // Group tools by body part (excluding control tools which are global)
  const groupedTools = useMemo(() => {
    const groups: Record<MovementBodyPart, ToolDefinition[]> = {
      head: [],
      body: [],
      leftArm: [],
      rightArm: [],
      leftLeg: [],
      rightLeg: [],
    };

    for (const tool of tools) {
      // Skip control tools - they are global, not puppet-specific
      if (tool.bodyPart === 'control') continue;
      if (tool.bodyPart in groups) {
        groups[tool.bodyPart as MovementBodyPart].push(tool);
      }
    }

    return groups;
  }, [tools]);

  const toggleGroup = (bodyPart: BodyPart) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(bodyPart)) {
        next.delete(bodyPart);
      } else {
        next.add(bodyPart);
      }
      return next;
    });
  };

  if (!puppet) {
    return (
      <div
        className="flex h-full items-center justify-center p-4 text-sm"
        style={{ color: 'var(--color-dark-text-muted)' }}
      >
        No puppet selected
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header with Add Tool button */}
      <div
        className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: 'var(--color-dark-border)' }}
      >
        <h3
          className="text-sm font-medium"
          style={{ color: 'var(--color-dark-text)' }}
        >
          Tools ({tools.length})
        </h3>
        <motion.button
          onClick={onAddTool}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium"
          style={{
            backgroundColor: 'var(--color-body-head)',
            color: 'white',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-label="Add tool"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Tool
        </motion.button>
      </div>

      {/* Tool groups - scrollable area */}
      <div className="flex-1 overflow-y-auto p-2">
        {BODY_PART_CONFIG.map((config) => {
          const groupTools = groupedTools[config.key];
          const isExpanded = expandedGroups.has(config.key);
          const hasTools = groupTools.length > 0;

          // Only show groups that have tools
          if (!hasTools) {
            return null;
          }

          return (
            <Collapsible.Root
              key={config.key}
              open={isExpanded}
              onOpenChange={() => toggleGroup(config.key)}
              data-testid={`tool-group-${config.key}`}
            >
              <Collapsible.Trigger asChild>
                <motion.button
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left"
                  style={{
                    backgroundColor: 'transparent',
                  }}
                  whileHover={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  {/* Expand/collapse icon */}
                  {isExpanded ? (
                    <ChevronDown
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: 'var(--color-dark-text-muted)' }}
                    />
                  ) : (
                    <ChevronRight
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: 'var(--color-dark-text-muted)' }}
                    />
                  )}

                  {/* Color indicator */}
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: config.color }}
                  />

                  {/* Label */}
                  <span
                    className="flex-1 text-sm font-medium"
                    style={{ color: 'var(--color-dark-text)' }}
                  >
                    {config.label}
                  </span>

                  {/* Tool count */}
                  <span
                    className="rounded-full px-2 py-0.5 text-xs"
                    style={{
                      backgroundColor: 'var(--color-dark-bg)',
                      color: 'var(--color-dark-text-muted)',
                    }}
                  >
                    {groupTools.length}
                  </span>
                </motion.button>
              </Collapsible.Trigger>

              <Collapsible.Content>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}
                      className="ml-6 space-y-1 overflow-hidden py-1"
                    >
                      {groupTools.map((tool) => (
                        <ToolListItem
                          key={tool.id}
                          tool={tool}
                          onEdit={() => onEditTool(tool)}
                          onDelete={() => onDeleteTool(tool)}
                          onDuplicate={() => onDuplicateTool(tool)}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </Collapsible.Content>
            </Collapsible.Root>
          );
        })}

        {/* Empty state if no tools */}
        {tools.length === 0 && (
          <div
            className="flex flex-col items-center justify-center py-8 text-center"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            <p className="mb-4 text-sm">No tools configured</p>
            <motion.button
              onClick={onAddTool}
              className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
              style={{
                backgroundColor: 'var(--color-dark-border)',
                color: 'var(--color-dark-text)',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="h-4 w-4" />
              Add First Tool
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
}
